import type { RefObject } from "react";
import { useEffect, useRef, useState } from "react";
import { IconHeart, IconHeartFilled } from "../components/Icons";
import { MatchScoreRing } from "../components/MatchScoreRing";
import { matchingData } from "../data";
import { useIsMobile } from "../hooks/useIsMobile";

type MatchingSectionProps = {
  sectionRef: RefObject<HTMLDivElement>;
};

export const MatchingSection = ({ sectionRef }: MatchingSectionProps) => {
  const isMobile = useIsMobile(960);
  const mobileTrackRef = useRef<HTMLDivElement>(null);
  const [mobileCardWidth, setMobileCardWidth] = useState(0);
  const [matchStep, setMatchStep] = useState(0);
  const stepCount = matchingData.homeowner.answers.length;
  const isComplete = matchStep >= stepCount;
  const progressValue = isComplete
    ? 85
    : Math.round((matchStep / stepCount) * 85);
  const mobileIndex = Math.min(matchStep, stepCount - 1);
  const mobileTranslate = -mobileIndex * mobileCardWidth;

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setMatchStep(stepCount);
      return undefined;
    }

    const timer = window.setInterval(() => {
      setMatchStep((prev) => (prev + 1) % (stepCount + 1));
    }, 2500);

    return () => window.clearInterval(timer);
  }, [stepCount]);

  useEffect(() => {
    if (!isMobile) {
      return undefined;
    }

    const update = () => {
      const width = mobileTrackRef.current?.clientWidth ?? 0;
      setMobileCardWidth(Math.max(0, Math.round(width)));
    };

    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, [isMobile]);

  return (
    <section
      ref={sectionRef}
      id="how-it-works"
      className="sg-section sg-matching"
    >
      <div className="sg-container">
        <div className="sg-section__header">
          <span className="sg-kicker">Watch the Magic Happen</span>
          <h2 className="sg-section__title sg-section__title--magenta">
            See How Matching Works
          </h2>
        <p className="sg-section__subtitle sg-matching__subtitle">
          Both sides complete their profiles, then Smart-Guild finds the
          perfect match.
        </p>
        </div>

        {isMobile ? (
          <div className="sg-matching__mobile">
            <div className="sg-matching__mobile-dots">
              {Array.from({ length: stepCount }).map((_, index) => (
                <span
                  key={`dot-${index}`}
                  className={[
                    "sg-matching__mobile-dot",
                    matchStep >= index ? "is-active" : "",
                    ""
                  ]
                    .filter(Boolean)
                    .join(" ")}
                />
              ))}
            </div>

            <div className="sg-matching__mobile-cards" ref={mobileTrackRef}>
              <div
                className="sg-matching__mobile-track"
                style={{ transform: `translateX(${mobileTranslate}px)` }}
              >
                {matchingData.homeowner.answers.map((homeItem, index) => {
                  const designerItem = matchingData.designer.answers[index];
                  const isVisible = matchStep >= index;
                  const isActive = matchStep === index;
                  const showAnswer = isVisible;

                  return (
                    <div
                      key={`mobile-${homeItem.section}-${index}`}
                      className={[
                        "sg-matching__mobile-card",
                        isVisible ? "is-visible" : "",
                        isActive ? "is-active" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                    <div className="sg-matching__mobile-badge">
                      <span>{homeItem.section}</span>
                      <span className="sg-matching__mobile-badge-heart">
                        <IconHeartFilled size={18} />
                      </span>
                    </div>
                      <div className="sg-matching__mobile-compare">
                        <div className="sg-matching__mobile-side">
                        <div className="sg-matching__mobile-side-label">
                          Homeowner
                        </div>
                          <div className="sg-matching__mobile-question">
                            {homeItem.question}
                          </div>
                        <div className="sg-matching__mobile-answer">
                          {showAnswer ? homeItem.answer : "—"}
                        </div>
                        </div>
                        <div className="sg-matching__mobile-side">
                        <div className="sg-matching__mobile-side-label">
                          Designer
                        </div>
                          <div className="sg-matching__mobile-question">
                            {designerItem.question}
                          </div>
                        <div className="sg-matching__mobile-answer">
                          {showAnswer ? designerItem.answer : "—"}
                        </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div
              className={[
                "sg-matching__mobile-result",
                isComplete ? "is-active" : "",
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <MatchScoreRing value={progressValue} size="large" sizePx={178} />
              <div
                className={[
                  "sg-matching__progress-label",
                  isComplete ? "is-complete" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {isComplete ? "Excellent Match!" : "Calculating..."}
              </div>
              <div className="sg-matching__progress-caption">
                Smart-Guild Matching Overall Rate
              </div>
            </div>
          </div>
        ) : (
          <div className="sg-matching__grid">
            <div className="sg-matching__column sg-matching__column--left">
              <div className="sg-matching__label">
                {matchingData.homeowner.label}
              </div>
              <div className="sg-matching__type">
                {matchingData.homeowner.personalityType}
              </div>
              <div className="sg-matching__items">
                {matchingData.homeowner.answers.map((item, index) => {
                  const isVisible = matchStep >= index;
                  const isActive = matchStep === index;
                  const isComplete = matchStep > index;
                  const heartActive = isVisible;

                  return (
                    <div
                      key={`${item.section}-${index}`}
                      className={[
                        "sg-matching__item",
                        isVisible ? "is-visible" : "",
                        isActive ? "is-active" : "",
                        isComplete ? "is-complete" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      <div className="sg-matching__item-header">
                        <span className="sg-matching__item-title">
                          {item.section}
                        </span>
                        <span
                          className={[
                            "sg-matching__item-heart",
                            heartActive ? "is-active" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                        >
                          {heartActive ? (
                            <IconHeartFilled size={20} />
                          ) : (
                            <IconHeart size={20} />
                          )}
                        </span>
                      </div>
                      <div className="sg-matching__item-question">
                        {item.question}
                      </div>
                      <div
                        className={[
                          "sg-matching__item-answer",
                          isVisible ? "is-visible" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        {item.answer}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="sg-matching__progress">
              <MatchScoreRing value={progressValue} size="large" sizePx={178} />
              <div
                className={[
                  "sg-matching__progress-label",
                  isComplete ? "is-complete" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {isComplete ? "Excellent Match!" : "Calculating..."}
              </div>
              <div className="sg-matching__progress-caption">
                Smart-Guild Matching Overall Rate
              </div>
            </div>

            <div className="sg-matching__column sg-matching__column--right">
              <div className="sg-matching__label">
                {matchingData.designer.label}
              </div>
              <div className="sg-matching__type">
                {matchingData.designer.personalityType}
              </div>
              <div className="sg-matching__items">
                {matchingData.designer.answers.map((item, index) => {
                  const isVisible = matchStep >= index;
                  const isActive = matchStep === index;
                  const isComplete = matchStep > index;
                  const heartActive = isVisible;

                  return (
                    <div
                      key={`${item.section}-${index}`}
                      className={[
                        "sg-matching__item",
                        isVisible ? "is-visible" : "",
                        isActive ? "is-active" : "",
                        isComplete ? "is-complete" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      <div className="sg-matching__item-header">
                        <span className="sg-matching__item-title">
                          {item.section}
                        </span>
                        <span
                          className={[
                            "sg-matching__item-heart",
                            heartActive ? "is-active" : "",
                          ]
                            .filter(Boolean)
                            .join(" ")}
                        >
                          {heartActive ? (
                            <IconHeartFilled size={20} />
                          ) : (
                            <IconHeart size={20} />
                          )}
                        </span>
                      </div>
                      <div className="sg-matching__item-question">
                        {item.question}
                      </div>
                      <div
                        className={[
                          "sg-matching__item-answer",
                          isVisible ? "is-visible" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        {item.answer}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
